/**
 * @fileoverview Vendor Invoice List Component
 * @module VendorInvoiceList
 * 
 * Business Context:
 * - Displays uploaded vendor invoices for a maintenance finding
 * - Shows amount, description, and provides download/delete actions
 * - Helps users track their documented repair costs
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FileText, Download, Trash2, Loader2, DollarSign, ExternalLink } from 'lucide-react';
import { apiClient } from '../../api/client';

interface VendorInvoice {
  id: string;
  fileName: string;
  amount: number; // in cents
  description: string;
  fileSize: number;
  uploadedAt: string;
}

interface VendorInvoiceListProps {
  invoices: VendorInvoice[];
  onDelete: () => void; // Callback to refresh list after deletion
}

const VendorInvoiceList: React.FC<VendorInvoiceListProps> = ({ invoices, onDelete }) => {
  const [loadingDownload, setLoadingDownload] = useState<string | null>(null);
  const [loadingDelete, setLoadingDelete] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const formatCurrency = (cents: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDownload = async (invoice: VendorInvoice) => {
    setLoadingDownload(invoice.id);
    try {
      const response = await apiClient.evidence.getInvoiceDownloadUrl(invoice.id);
      if (response.data.success) {
        // Open the download URL in a new tab
        window.open(response.data.data.downloadUrl, '_blank');
      }
    } catch (error: any) {
      console.error('Error getting download URL:', error);
      const msg = error?.response?.data?.error?.message;
      toast.error(msg || 'Failed to download invoice. Please try again.');
    } finally {
      setLoadingDownload(null);
    }
  };

  const handleDelete = async (invoiceId: string) => {
    if (deleteConfirm !== invoiceId) {
      setDeleteConfirm(invoiceId);
      // Auto-clear confirmation after 3 seconds
      setTimeout(() => setDeleteConfirm(null), 3000);
      return;
    }

    setLoadingDelete(invoiceId);
    try {
      await apiClient.evidence.deleteInvoice(invoiceId);
      setDeleteConfirm(null);
      onDelete();
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      const msg = error?.response?.data?.error?.message;
      toast.error(msg || 'Failed to delete invoice. Please try again.');
    } finally {
      setLoadingDelete(null);
    }
  };

  // Calculate total
  const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  if (invoices.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {invoices.map((invoice) => (
        <div
          key={invoice.id}
          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">{invoice.fileName}</h4>
                  <p className="text-sm text-gray-600 mt-1 line-clamp-2">{invoice.description}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="flex items-center gap-1 text-green-600 font-semibold">
                    <DollarSign className="w-4 h-4" />
                    <span>{formatCurrency(invoice.amount).replace('$', '')}</span>
                  </div>
                </div>
              </div>

              {/* Meta info */}
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <span>{formatFileSize(invoice.fileSize)}</span>
                <span>•</span>
                <span>Uploaded {formatDate(invoice.uploadedAt)}</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={() => handleDownload(invoice)}
                  disabled={loadingDownload === invoice.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loadingDownload === invoice.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4" />
                  )}
                  <span>View</span>
                </button>

                <button
                  onClick={() => handleDelete(invoice.id)}
                  disabled={loadingDelete === invoice.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors disabled:opacity-50 ${
                    deleteConfirm === invoice.id
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-100 hover:bg-red-100 text-gray-700 hover:text-red-700'
                  }`}
                >
                  {loadingDelete === invoice.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  <span>{deleteConfirm === invoice.id ? 'Confirm Delete' : 'Delete'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Total Summary */}
      {invoices.length > 1 && (
        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
          <span className="font-medium text-green-800">Total Documented Cost</span>
          <span className="text-lg font-bold text-green-700">{formatCurrency(totalAmount)}</span>
        </div>
      )}
    </div>
  );
};

export default VendorInvoiceList;







